import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  FaUsers,
  FaClock,
  FaChartLine,
  FaLayerGroup,
  FaCheckCircle
} from 'react-icons/fa';
import MetricKpiCard from '../MetricKpiCard';
import TopFeaturesHorizontalBar from '../charts/TopFeaturesHorizontalBar';
import FlowEnvironmentPanel from '../FlowEnvironmentPanel';
import { formatDurationSeconds } from '../../shared/analyticsViewUtils';

export default function OverviewTab({
  overview = {},
  featureAdoption = [],
  flows = [],
  loading = false,
  filters = {}
}) {
  const [activeUsersGranularity, setActiveUsersGranularity] = useState('WAU'); // 'DAU' | 'WAU'

  const { activeUsers, sessionMetrics } = overview || {};

  const dau = Number(activeUsers?.dailyActiveUsers || 0);
  const wau = Number(activeUsers?.weeklyActiveUsers || 0);

  // Compute active query window duration to handle backend date clamping
  const getWindowDays = () => {
    if (filters?.preset === 'today') return 1;
    if (filters?.preset === '3d') return 3;
    if (filters?.preset === '7d') return 7;
    if (filters?.preset === '30d') return 30;
    const from = filters?.fromUtc || activeUsers?.windowStartUtc;
    const to = filters?.toUtc || activeUsers?.windowEndUtc;
    if (!from || !to) return 7;
    const fromDateOnly = from.split('T')[0];
    const toDateOnly = to.split('T')[0];
    if (fromDateOnly === toDateOnly) return 1;
    const start = new Date(fromDateOnly).getTime();
    const end = new Date(toDateOnly).getTime();
    if (isNaN(start) || isNaN(end) || end < start) return 7;
    const diffDays = Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1;
    return Math.max(1, Math.min(diffDays, 30));
  };

  const windowDays = getWindowDays();
  const isSingleDay = windowDays <= 1;

  const isWau = activeUsersGranularity === 'WAU';
  const displayedActiveUsers = isSingleDay ? (dau || wau) : (isWau ? wau : dau);

  let activeUsersSubtitle = '';
  let activeUsersTooltip = '';

  if (isSingleDay) {
    activeUsersSubtitle = 'Unique active users for selected day';
    activeUsersTooltip = `Measures unique active individuals during the selected 24-hour window (${(dau || wau).toLocaleString()}). Note: Because a single-day window was selected, backend Weekly and Monthly metrics are clamped to this single day.`;
  } else if (isWau) {
    activeUsersSubtitle = windowDays >= 7
      ? 'Active users in past 7 days (WAU)'
      : `Active users across ${windowDays}-day window`;
    activeUsersTooltip = windowDays >= 7
      ? `Measures distinct individuals active in your organization. WAU reflects unique users across the 7-day window (${wau.toLocaleString()}); DAU reflects unique users active in the past 24 hours (${dau.toLocaleString()}).`
      : `Measures distinct individuals active across the selected ${windowDays}-day window (${wau.toLocaleString()}); DAU reflects unique users active in the past 24 hours (${dau.toLocaleString()}).`;
  } else {
    activeUsersSubtitle = 'Active users in past 24 hours (DAU)';
    activeUsersTooltip = `Measures unique individuals active in the past 24 hours (${dau.toLocaleString()}). Active users across the full ${windowDays}-day window: ${wau.toLocaleString()}.`;
  }

  const stickiness = (!isSingleDay && wau > 0) ? `${((dau / wau) * 100).toFixed(1)}%` : null;

  const totalSessions = sessionMetrics?.sessionCount !== undefined
    ? Number(sessionMetrics.sessionCount).toLocaleString()
    : '0';

  const rawDurationSeconds = Number(sessionMetrics?.averageSessionDurationSeconds || 0);
  const avgDurationFormatted = sessionMetrics?.formattedDuration || formatDurationSeconds(rawDurationSeconds);

  const validFeatures = Array.isArray(featureAdoption) ? featureAdoption : [];
  const avgAdoption = validFeatures.length > 0
    ? (
        (validFeatures.reduce((acc, f) => acc + Number(f.adoptionRate || 0), 0) /
          validFeatures.length) *
        100
      ).toFixed(1)
    : '0';

  return (
    <div className="tab-content-pane overview-tab flex flex-col gap-6" data-testid="overview-tab">
      {/* Top Section: Client Runtime & Hardware Footprint */}
      <FlowEnvironmentPanel
        filters={filters}
        title="Runtime & hardware telemetry"
      />

      {/* Top Row: Metric KPI Cards */}
      <div className="kpi-grid">
        <MetricKpiCard
          title="Active Users"
          value={displayedActiveUsers.toLocaleString()}
          subtitle={activeUsersSubtitle}
          icon={<FaUsers />}
          color="indigo"
          loading={loading}
          tooltip={activeUsersTooltip}
          badge={
            isSingleDay ? (
              <span className="single-day-badge" data-testid="single-day-badge">
                1-Day Window
              </span>
            ) : (
              <div className="granularity-mini-selector" role="group" aria-label="Select active user window">
                {['DAU', 'WAU'].map((gran) => (
                  <button
                    key={gran}
                    type="button"
                    className={`mini-gran-btn ${activeUsersGranularity === gran ? 'active' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveUsersGranularity(gran);
                    }}
                    data-testid={`gran-${gran}`}
                  >
                    {gran}
                  </button>
                ))}
              </div>
            )
          }
        />

        <MetricKpiCard
          title="Total Sessions"
          value={totalSessions}
          subtitle="Recorded runtime and admin sessions"
          icon={<FaLayerGroup />}
          color="blue"
          loading={loading}
          tooltip="Total distinct session instances initiated across web players, runtime extensions, and admin modules within the active window."
        />

        <MetricKpiCard
          title="Avg. Session Duration"
          value={avgDurationFormatted}
          subtitle="Average active user session length"
          icon={<FaClock />}
          color="violet"
          loading={loading}
          tooltip={`Average active time users spent engaging with IAP per session${rawDurationSeconds >= 60 ? ` (${Math.round(rawDurationSeconds).toLocaleString()}s total)` : ''}, measured from start to completion or timeout.`}
        />

        <MetricKpiCard
          title="Feature Adoption"
          value={`${avgAdoption}%`}
          subtitle={`${validFeatures.length} capabilities tracked`}
          icon={<FaChartLine />}
          color="emerald"
          loading={loading}
          tooltip={`Average adoption percentage across all ${validFeatures.length} tracked capabilities.${stickiness ? ` Includes Daily-to-Weekly stickiness (${stickiness}).` : ''}`}
          badge={
            stickiness ? (
              <span className="stickiness-chip" title="Daily stickiness: DAU to WAU ratio">
                <FaCheckCircle className="chip-icon" /> {stickiness} Stickiness
              </span>
            ) : isSingleDay ? (
              <span className="stickiness-chip" title="Stickiness ratio requires multi-day window">
                <FaCheckCircle className="chip-icon" /> 1-Day View
              </span>
            ) : null
          }
        />
      </div>

      {/* Visual Analytics Charts: Full-width Top Features Engagement */}
      <div className="charts-single-grid">
        <TopFeaturesHorizontalBar
          features={validFeatures}
          flows={flows}
          height={340}
        />
      </div>
    </div>
  );
}

OverviewTab.propTypes = {
  overview: PropTypes.shape({
    usageSummary: PropTypes.object,
    activeUsers: PropTypes.object,
    sessionMetrics: PropTypes.object,
    isEntitled: PropTypes.bool,
    errors: PropTypes.object
  }),
  featureAdoption: PropTypes.array,
  flows: PropTypes.array,
  loading: PropTypes.bool,
  filters: PropTypes.shape({
    organizationId: PropTypes.string,
    siteCollectionId: PropTypes.string,
    fromUtc: PropTypes.string,
    toUtc: PropTypes.string,
    preset: PropTypes.string
  })
};
